import { SpaceBetween } from '@cloudscape-design/components';
import React, { useState, useEffect } from 'react';
import { BaseCloudscapeForm, BaseFormField, CustomFieldComponentProps } from './base-form';
import { FormField, ColumnLayout, DatePicker, Alert } from '@cloudscape-design/components';
import { submissionsApi } from 'api/Submission';

interface DateTimeValue {
  date: string;
}

interface DateTimeFieldProps extends CustomFieldComponentProps<DateTimeValue> {}

const PARENT_ACCOUNT_DEFAULT_VALUE = { label: "No Organization Account", value: "-1" }; 

const ContractEndDateField: React.FC<DateTimeFieldProps> = ({
  value,
  onChange,
  errorText,
  isSubmitted,
  disabled
}) => {
  return (
    <FormField
      errorText={isSubmitted ? errorText : undefined}
      i18nStrings={{ errorIconAriaLabel: 'Error' }}
    >
      <DatePicker
        value={value.date}
        onChange={({ detail }) => onChange({ date: detail.value })}
        disabled={disabled}
      />
    </FormField>
  );
};

// Form data interface
interface ExternalAccountData {
  target_account_id: string;
  parent_account_id?: any;
  account_name?: string;
  contract_end_datetime?: DateTimeValue;
  is_active: { label: string; value: boolean; };
  is_organization: { label: string; value: boolean; };
  
}

interface ExternalAccountFormProps {
  accountId?: string;
}

export const ExternalAccountForm: React.FC<ExternalAccountFormProps> = ({ accountId }) => {
  const [parentAccounts, setParentAccounts] = useState<Array<{ id: string, account_name: string, target_account_id: string }>>([]);
  const [initialValues, setInitialValues] = useState<ExternalAccountData>({
    target_account_id: '',
    parent_account_id: PARENT_ACCOUNT_DEFAULT_VALUE,
    account_name: '',
    contract_end_datetime: {
      date: new Date().toISOString().split('T')[0]
    },
    is_active: { label: 'Inactive', value: false },
    is_organization: { label: 'Organization', value: true },

  });
  const [isLoading, setIsLoading] = useState(!!accountId);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);


  useEffect(() => {
    const fetchParentAccounts = async () => {
      try {
        const data = await submissionsApi.getExternalAccounts();
        const formattedAccounts = data.accounts.map((account: any) => ({
          id: account.id,
          account_name: account.account_name,
          target_account_id: account.target_account_id
        }));
        setParentAccounts(formattedAccounts);
      } catch (error) {
        console.error('Error fetching parent accounts:', error);
      }
    };

    const fetchAccountData = async () => {
      if (accountId) {
        try {
          const data = await submissionsApi.getExternalAccount(accountId);

          setInitialValues({
            target_account_id: data.target_account_id,
            parent_account_id: data.parent_account_id ? { label: data.parent_account_name, value: data.parent_account_id } : PARENT_ACCOUNT_DEFAULT_VALUE,
            account_name: data.account_name || '',
            contract_end_datetime: {
              date: data.contract_end_datetime ? 
                data.contract_end_datetime.split('T')[0] : 
                new Date().toISOString().split('T')[0]
            },
            is_organization: { 
              label: data.is_organization ? 'Organization' : 'Sub-Account', 
              value: data.is_organization 
            },
            is_active: { 
                label: data.is_active ? 'Active' : 'Inactive', 
                value: data.is_active 
              }
          });
        } catch (error) {
          console.error('Error fetching account data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchParentAccounts();
    fetchAccountData();
  }, [accountId]);

  const createExternalAccountFields: BaseFormField[] = [
    {
      name: 'target_account_id',
      label: 'AWS Account ID',
      type: 'text',
      isCustom: false,
      required: true,
      disabled: (formValues?: Record<string, any>) => accountId ? true : false,
      placeholder: 'Enter AWS account ID',
      validation: (value: string) => {
        if (!/^\d{12}$/.test(value)) {
          return 'Please enter a valid 12-digit AWS account ID';
        }
      }
    },
    {
      name: 'parent_account_id',
      label: 'Organization Account',
      type: 'select',
      isCustom: false,
      required: false,
      options: [
        PARENT_ACCOUNT_DEFAULT_VALUE,
        ...parentAccounts.map(account => ({
          label: `${account.account_name} (${account.target_account_id})`,
          value: account.id
        }))
      ],
      placeholder: 'Select parent account (optional)',
      validation: (value: any, formValues?: Record<string, any>) => {
        if (!formValues?.is_organization.value && value === PARENT_ACCOUNT_DEFAULT_VALUE) {
          return 'Organization Parent Account is required when No Organization Account is selected';
        }
      },
      disabled: (formValues?: Record<string, any>) => formValues?.is_organization?.value === true,

    },
    {
      name: 'account_name',
      label: 'Account Name',
      type: 'text',
      isCustom: false,
      required: true,
      placeholder: 'Enter account name',
      // validation: (value: string, formValues?: Record<string, any>) => {
      //   if (formValues?.parent_account_id.value === PARENT_ACCOUNT_DEFAULT_VALUE.value ) {
      //     return 'Account name is required when No Organization Account is selected';
      //   }
      // }
    },
    {
      name: 'contract_end_datetime',
      label: 'Contract End Date',
      type: 'select',
      isCustom: true,
      required: false,
      customComponent: ContractEndDateField,
      disabled: (formValues?: Record<string, any>) => formValues?.is_organization?.value === false,
      validation: (value: DateTimeValue | undefined, formValues?: Record<string, any>) => {
        if (!formValues?.parent_account_id && !value?.date) {
          return 'Contract end date is required when No Organization Account is selected';
        }
      }
    },
    {
      name: 'is_organization',
      label: 'Account Type',
      type: 'select',
      isCustom: false,
      required: true,
      options: [
        { label: 'Organization', value: true },
        { label: 'Sub-Account', value: false }
      ],
      description: 'Is the account an organization or a sub-account?'
    },
    {
      name: 'is_active',
      label: 'Active Status',
      type: 'select',
      isCustom: false,
      required: true,
      options: [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false }
      ],
      description: 'Set the active status of the account'
    }
  ];
  const handleSubmit = async (values: ExternalAccountData) => {
    try {
      const submissionData = {
        target_account_id: values.target_account_id,
        parent_account_id: values.parent_account_id.value === PARENT_ACCOUNT_DEFAULT_VALUE.value ? 
          null : 
          values.parent_account_id.value,
        account_name: values.account_name || null,
        contract_end_datetime: (values.contract_end_datetime && values.is_organization.value) ? 
          `${values.contract_end_datetime.date}T12:00:00.000Z` : 
          null,
        is_active: Boolean(values.is_active?.value),
        is_organization: values.is_organization.value,
      };
  
      if (accountId) {
        await submissionsApi.updateExternalAccount(accountId, submissionData);
      } else {
        const result = await submissionsApi.createExternalAccount(submissionData);
        // Only redirect on creation
        window.location.href = '/external-accounts';
      }
  
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'DUPLICATE_ACCOUNT') {
          setErrorMessage("External account with same Target Id exists");
        } else {
          setErrorMessage("An error occurred while saving the account");
          console.error('Submission error:', error);
        }
      }
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <SpaceBetween direction="vertical" size="l">
    {errorMessage && (
        <Alert type='error' className="mb-4">
            {errorMessage}
        </Alert>
    )}

    <BaseCloudscapeForm<ExternalAccountData>
      fields={createExternalAccountFields}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={() => window.location.href = '/external-accounts'}
      submitButtonText={accountId ? "Update Account" : "Create Account"}
      cancelButtonText="Cancel"
    />
    </SpaceBetween>

  );
};